import { useMemo } from 'react';

import {
  ChartTimeSeriesLine,
  ChartTimeSeriesLineProps,
  ChartTimeSeriesLineTheme,
} from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { DateStr } from '@hcs/types';
import { formatPercentD3, getD3PercentFormat } from '@hcs/utils';
import { calcMobileMarginAdjustment } from '@hcs/utils';

import { MACRO_INSIGHTS_FROM_DATE } from '../../constants/marketInsights.constants';
import { useMsaUnemploymentTimeSeries } from '../../hooks/useMsaUnemploymentTimeSeries';

const D3_FORMAT_CONFIG = { precision: 1 };
const D3_FORMAT_STR = getD3PercentFormat(D3_FORMAT_CONFIG);

const dataHcName = 'msa-unemployment-chart';
interface MsaUnemploymentChartProps {
  className?: string;
  msaId: string;
  fromDate?: DateStr;
  toDate?: DateStr;
  theme?: Partial<ChartTimeSeriesLineTheme>;
}
export const MsaUnemploymentChart = ({
  msaId,
  className,
  fromDate = MACRO_INSIGHTS_FROM_DATE,
  toDate,
  theme,
}: MsaUnemploymentChartProps) => {
  const { data, isLoading } = useMsaUnemploymentTimeSeries({
    msaId,
    fromDate,
    toDate,
  });
  const { isPhoneWidth } = useScreenSize();
  const timeSeriesData =
    data?.lookupMsaDetails?.populationStats?.unemploymentRate?.timeSeries;
  const chartData: ChartTimeSeriesLineProps['chartData'] = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return null;
    }
    return [
      {
        id: 'unemployment',
        data: timeSeriesData.map((record) => {
          return {
            x: `${record.month}`,
            y: record.unemploymentRate != null ? record.unemploymentRate : null,
          };
        }),
      },
    ];
  }, [timeSeriesData]);
  // current value should be the last record
  const currentUnemploymentRate = timeSeriesData
    ? timeSeriesData[timeSeriesData.length - 1]
    : null;

  if (!isLoading && (!timeSeriesData || timeSeriesData.length === 0)) {
    return null;
  }

  const mergedTheme = {
    Container: className,
    ...theme,
  };

  const mobileMargin =
    isPhoneWidth && chartData?.[0].data
      ? calcMobileMarginAdjustment(chartData?.[0].data, D3_FORMAT_STR)
      : undefined;

  return (
    <ChartTimeSeriesLine
      dataHcName={dataHcName}
      isLoading={isLoading}
      chartData={chartData}
      rightAxisMargin={mobileMargin}
      title="Unemployment Rate"
      CurrentValue={
        <>
          {formatPercentD3(
            currentUnemploymentRate?.unemploymentRate,
            D3_FORMAT_CONFIG,
          )}
        </>
      }
      d3FormatterForY={D3_FORMAT_STR}
      theme={mergedTheme}
      compactTitle={isPhoneWidth}
      compactValue={isPhoneWidth}
    />
  );
};
